import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { EmptyState } from "@/components/ui";
import type { FileTransferInfo } from "../types/browser-session";

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

function FileList({
  files,
  icon,
  emptyText,
  label,
}: {
  files: FileTransferInfo[] | null;
  icon: React.ReactNode;
  emptyText: string;
  label: string;
}) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle2">{label}</Typography>
        {files && files.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            ({files.length})
          </Typography>
        )}
      </Box>
      {!files || files.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 4, py: 1 }}>
          {emptyText}
        </Typography>
      ) : (
        <List dense disablePadding>
          {files.map((file) => (
            <ListItem
              key={file.filename}
              secondaryAction={
                <Tooltip title="Download">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => window.open(file.url, "_blank")}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary={file.filename}
                secondary={[file.mime_type, formatBytes(file.size)]
                  .filter(Boolean)
                  .join(" \u00B7 ")}
                primaryTypographyProps={{ variant: "body2", noWrap: true }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

export function SessionFilesTab({
  downloads,
  uploads,
}: {
  downloads: FileTransferInfo[] | null;
  uploads: FileTransferInfo[] | null;
}) {
  const hasAny = (downloads && downloads.length > 0) || (uploads && uploads.length > 0);

  if (!hasAny) {
    return <EmptyState message="No files transferred during this session" sx={{ p: 3 }} />;
  }

  return (
    <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <FileList
        files={downloads}
        icon={<FileDownloadIcon fontSize="small" color="primary" />}
        label="Downloads"
        emptyText="No downloads"
      />
      <FileList
        files={uploads}
        icon={<FileUploadIcon fontSize="small" color="secondary" />}
        label="Uploads"
        emptyText="No uploads"
      />
    </Box>
  );
}
